import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { updateBackend } from "../../actions/backendVersionActions";
import { useCompatibilityMatrix } from "../../context/compatibilityMatrixContext";
import { useTranslation } from "../../context/TranslationContext";

import { useBackendStatusContext } from "../../context/backendStatus/backendStatusContext";
import {
	SET_BACKEND_UNAVAILABLE,
	SET_BACKEND_AVAILABLE,
	SET_BACKEND_VERSION,
	SET_UPDATING,
	SET_UPDATE_FINISHED,
	SET_UPDATE_REQUESTED,
	FETCH_COMPATIBLE_VERSIONS_SUCCESS,
	FETCH_COMPATIBLE_VERSIONS_FAILURE,
} from "../../context/backendStatus/backendStatusActions";
import { useAuthContext } from "../../context/AuthContext";
import { useAppContext } from "../../context/appContext";
import { usePollingManager } from "../../context/PollingManagerContext";
import { TestWorkflowAction } from "../../constants/testWorkflowState";
import { useAppWorkflowContext } from "../../context/AppWorkflowContext";
import { StyledDialog, IconWrapper } from "../UpdateWarningModal/styles";
import ModalContent from "./ModalContent";
import ModalIcon from "./ModalIcon";
import soundManager from "../../utils/SoundManager";
import { TestWorkflowState } from "../../constants/testWorkflowState";

const UpdateModal = () => {
	const backendStatus = useBackendStatusContext();
	const { matrix } = useCompatibilityMatrix();
	// Access userData from AuthContext
	const { userData } = useAuthContext();
	const { toggleCompatibility, isLocalHost, isAcquireRunning } = useAppContext();
	const { workflowState, dispatchWorkflow } = useAppWorkflowContext();
	const { backendStatus: polledBackendVersion } = usePollingManager();
	const savedCompatibility = localStorage.getItem("compatibility");
	const { translate } = useTranslation();
	const hasUpdateStarted = useRef(false);
	const [initialIsUpdating, setInitialIsUpdating] = useState(() => {
		return localStorage.getItem("showUpdateInProgressModal") === "true";
	});

	const handleSwitchChange = (event) => {
		const isChecked = event.target.checked;
		toggleCompatibility(isChecked);
		// eslint-disable-next-line no-restricted-globals
		location.reload();
	};

	useEffect(() => {
		const isNowUpdating = backendStatus.state.isUpdating;

		if (isNowUpdating) {
			hasUpdateStarted.current = true;
		}

		if (hasUpdateStarted.current && !isNowUpdating) {
			soundManager.play("success");
			hasUpdateStarted.current = false;
		}

	}, [backendStatus.state.isUpdating]);

	useEffect(() => {
		if (!matrix || !userData || !backendStatus.state.version) return;
		const isPreReleaseTester = userData.groups?.includes("Reyedar-pre-release");

		backendStatus.dispatch({
			type: FETCH_COMPATIBLE_VERSIONS_SUCCESS,
			payload: { data: matrix, isPreReleaseTester },
		});

	}, [matrix, userData, backendStatus.state.version, workflowState]);

	useEffect(() => {
		// Skip if no version, a test is running, or if the user is part of a restricted group
		if (!polledBackendVersion || isAcquireRunning || userData?.groups?.includes("Reyedar-tester")) return;

		const backendVersion = polledBackendVersion;

		if (backendVersion.isAvailable)
			backendStatus.dispatch({ type: SET_BACKEND_AVAILABLE });
		else
			backendStatus.dispatch({ type: SET_BACKEND_UNAVAILABLE });

		if (backendVersion.version)
			backendStatus.dispatch({
				type: SET_BACKEND_VERSION,
				payload: { version: backendVersion.version },
			});

		if (backendVersion.version === "Not able to read version.") {
			backendStatus.dispatch({ type: SET_UPDATING });
		} else if (backendStatus.state.isUpdating && backendVersion.version) {
			backendStatus.dispatch({
				type: SET_UPDATE_FINISHED,
				payload: {
					message: `Backend updated successfully to version: ${backendVersion.version}`,
					status: "Success",
				},
			});
			dispatchWorkflow({ type: TestWorkflowAction.ENTER_IDLE });
		}
	}, [
		polledBackendVersion,
		isAcquireRunning,
		userData,
	]);

	const handleUpdate = async () => {
		const { targetVersion } = backendStatus.state;
		backendStatus.dispatch({ type: SET_UPDATE_REQUESTED });
		dispatchWorkflow({ type: TestWorkflowAction.ENTER_BACKEND_UPDATING });
		await updateBackend(targetVersion);
	};

	useEffect(() => {
		if (backendStatus.state.isUpdating || backendStatus.state.isUpdateRequested) {
			setInitialIsUpdating(true);
		} else {
			setInitialIsUpdating(false);
		}
	}, [backendStatus.state.isUpdating]);

	const isUpdateFlow =
		initialIsUpdating ||
		workflowState === TestWorkflowState.BACKEND_UPDATING;

	const isModalOpen =
		backendStatus.state.isUpdating ||
		(isUpdateFlow && !backendStatus.state.isUpdateRequired && backendStatus.state.isBackendCompatibleWithFrontend !== false) ||
		backendStatus.state.isUpdateRequired ||
		backendStatus.state.isBackendCompatibleWithFrontend === false; // explicitly check for false

	return (
		<StyledDialog fullWidth open={isModalOpen} onClose={(event, reason) => {
			if (reason !== "backdropClick" && backendStatus.state.isBackendCompatibleWithFrontend !== false) {
			}
		}}>
			<IconWrapper>
				<ModalIcon
					isUpdating={backendStatus.state.isUpdating}
					isUpdateRequired={backendStatus.state.isUpdateRequired}
					isUpdateRequested={backendStatus.state.isUpdateRequested}
					isBackendCompatible={backendStatus.state.isBackendCompatibleWithFrontend}
				/>
			</IconWrapper>
			<Box p={2} sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 0 }}>
				<ModalContent
					backendStatus={backendStatus}
					translate={translate}
					handleUpdate={handleUpdate}
					isLocalHost={isLocalHost}
					savedCompatibility={savedCompatibility}
					handleSwitchChange={handleSwitchChange}
				/>
			</Box>
		</StyledDialog>
	);
};

export default UpdateModal;
